import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Editor, descentTarget, type Altitude, diagramShown } from './canvas/Editor.js';
import { parseRoute, routePath, type Route } from './routes.js';
import { compositionToFlow, graphToFlow } from './canvas/model.js';
import { diagramToFlow } from './canvas/diagram.js';
import { codeSources } from './canvas/working-set.js';
import { ALTITUDE_NAMES, altitudeOf, altitudesOf, footprint } from './canvas/altitude.js';
import type { FlowAltitude } from '@civil/schema';
import { exposesSync } from './canvas/edges.js';
import type { NodeData } from './canvas/nodes.js';
/**
 * Monaco is about a megabyte gzipped — more than the rest of Civil combined. Loading
 * it eagerly would make every canvas session pay for an editor it may never open, so
 * it arrives on the first descent into code and is cached from then on.
 */
const CodeContext = lazy(() =>
  import('./code/CodeContext.js').then((m) => ({ default: m.CodeContext })),
);
/** The diff panel embeds Monaco's diff editor, so it arrives with the same chunk. */
const DiffPanel = lazy(() =>
  import('./panes/DiffPanel.js').then((m) => ({ default: m.DiffPanel })),
);
import { ApplyButton } from './panes/ApplyButton.js';
import { GENERATION_UNSUPPORTED, SourceControl } from './panes/SourceControl.js';
import { ConfirmDelete } from './panes/ConfirmDelete.js';
import { KeyHelp } from './commands/KeyHelp.js';
import { ProgressBar, Toast, type LiveProgress } from './commands/Toast.js';
import { liftAnalysisNote,
  liftInterpretationNote, liftProgressFraction, liftProgressText } from './lift.js';
import { useCommands } from './commands/useCommands.js';
import { commandById, formatChord, titleOf, type CommandContext } from './commands/registry.js';
import { Home } from './panes/Home.js';
import { AddNode } from './panes/AddNode.js';
import { ProjectPicker } from './panes/ProjectPicker.js';
import { RunPanel } from './panes/RunPanel.js';
import { RunLog } from './panes/RunLog.js';
import { PreviewPane, type AppSessionState } from './panes/PreviewPane.js';
import { SessionLog } from './panes/SessionLog.js';
import { Inspector } from './panes/Inspector.js';
import { ProjectTree } from './panes/ProjectTree.js';
import { AccountMenu } from './panes/AccountMenu.js';
import { Admin } from './panes/Admin.js';
import { Settings } from './panes/Settings.js';
import { usePanes } from './layout.js';
import { Tldr, type TldrState } from './panes/Tldr.js';
import { Chat, type ChatState } from './panes/Chat.js';
import { UiTab, type UiTabHandle, type UiTabState } from './panes/UiTab.js';
import { askChat, fetchChat, fetchTldr, startNewChat } from './ask.js';
import {
  SIGN_IN_ERRORS,
  fetchMe,
  requestAccess,
  signIn,
  signOut,
  type Me,
  type SessionState,
} from './identity.js';
import {
  applyOps,
  commitProject,
  initializeProject,
  liftRepo,
  watchLiftRun,
  type LiftRunProgress,
  fetchBundle,
  fetchExamples,
  fetchProjects,
  revertFile,
  saveFile,
  startRun,
  fetchRunEvents,
  cancelRun,
  startSession,
  getSession,
  stopSession,
  syncProject,
  checkGit,
  fetchGit,
  resolveConflict,
  fetchBranches,
  createBranch,
  switchToBranch,
  openPullRequest,
  type BranchList,
  type GitCheck,
  type GitInfo,
  transpileProject,
  liftGraph,
  migrateProject,
  type ManifestOp,
  type RunEvent,
  type RunSummary,
  type ProjectBundle,
  type ProjectSummary,
} from './project.js';

/**
 * One undoable step: which file an op batch touched, what the file said before it,
 * and whether that earlier state was itself a pending edit. Undo either restores the
 * earlier pending content or, when the batch was the first thing to touch the file,
 * discards the pending row so the file falls back to HEAD.
 */
interface UndoEntry {
  path: string;
  previous: string;
  hadPending: boolean;
  summary: string;
}

/** Enough for a long session of canvas edits; manifests are small. */
const UNDO_LIMIT = 50;

export function App(): React.ReactElement {
  const [session, setSession] = useState<SessionState>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    void fetchMe(controller.signal).then(setSession);
    return () => controller.abort();
  }, []);

  switch (session.status) {
    case 'loading':
      return <div className="gate" />;
    case 'signedOut':
      return <SignIn />;
    case 'error':
      return <Fatal title="Cannot reach the API" message={session.message} />;
    case 'authenticated':
      // Signed in is not let in (prd-deltas §22): the server refuses everything
      // else, so the IDE would only render broken.
      if (session.me.access !== 'active') {
        return <Waiting me={session.me} onRequested={() => void fetchMe().then(setSession)} />;
      }
      // The admin page is its own place, outside the IDE's project routing. The
      // server decides who is an admin; this only avoids drawing a page that would
      // answer 403.
      if (/^\/admin(\/|$)/.test(window.location.pathname) && session.me.admin) return <Admin me={session.me} />;
      return <Workspace me={session.me} />;
  }
}

/**
 * Signed in, not let in (prd-deltas §22). Asking is an act: signing in alone puts
 * nobody on the admin's list.
 */
function Waiting({ me, onRequested }: { me: Me; onRequested: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const ask = () => {
    setBusy(true);
    setFailure(null);
    void requestAccess(note)
      .then(onRequested)
      .catch((error: unknown) => setFailure((error as Error).message))
      .finally(() => setBusy(false));
  };

  return (
    <div className="gate">
      <div className="gate-card waiting">
        <h1>Civil</h1>
        {me.access === 'blocked' ? (
          <p>This account does not have access to Civil.</p>
        ) : me.accessRequestedAt ? (
          <p>
            You asked to be let in on {new Date(me.accessRequestedAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}{' '}
            as <strong>{me.email}</strong>. You will get in as soon as an admin lets you in.
          </p>
        ) : (
          <>
            <p>
              Civil is by invitation for now. You are signed in as <strong>{me.email}</strong>;
              ask, and an admin will let you in.
            </p>
            <textarea
              className="waiting-note"
              placeholder="What would you like to use Civil for? (optional)"
              value={note}
              maxLength={500}
              onChange={(event) => setNote(event.target.value)}
              aria-label="A note for the admin"
            />
            <button type="button" className="primary" disabled={busy} onClick={ask}>
              {busy ? 'Asking…' : 'Request access'}
            </button>
          </>
        )}
        {failure ? <p className="gate-error">{failure}</p> : null}
        <p>
          <button type="button" className="link" onClick={() => void signOut()}>
            Sign out
          </button>
        </p>
      </div>
    </div>
  );
}

function SignIn() {
  const error = new URLSearchParams(window.location.search).get('error');
  const message = error ? (SIGN_IN_ERRORS[error] ?? 'Sign-in failed. Try again.') : null;

  return (
    <div className="gate">
      <div className="gate-card">
        <h1>Civil</h1>
        <p>A web IDE for building applications at graph altitude.</p>
        {message ? <p className="gate-error">{message}</p> : null}
        <button type="button" onClick={signIn}>
          Continue with Google
        </button>
      </div>
    </div>
  );
}

function Fatal({ title, message }: { title: string; message: string }) {
  return (
    <div className="gate">
      <div className="gate-card">
        <h1>{title}</h1>
        <p>{message}</p>
        <button type="button" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    </div>
  );
}

type Load =
  | { status: 'loading' }
  | { status: 'empty' }
  | { status: 'ready'; bundle: ProjectBundle }
  | { status: 'error'; message: string };

const ACTIVE_PROJECT_KEY = 'civil.activeProject';

/** Views in reading order: what the application is, then how it is built, then the rest. */
const viewOrder = (ids: string[]): string[] => {
  const rank = (id: string) => (id === 'flow' ? 0 : id === 'architecture' ? 1 : id === 'concept' ? 2 : 3);
  return [...ids].sort((a, b) => rank(a) - rank(b) || (a < b ? -1 : a > b ? 1 : 0));
};

/** Where the address bar says to start; read once, before state exists. */
const BOOT_ROUTE = parseRoute(window.location.pathname);

/**
 * Which of a project's pages a route is (prd-deltas §24): the TL;DR it lands on, the
 * chat, or the canvas — the parts as written, a view, or a graph inside them.
 */
type Page = 'tldr' | 'canvas' | 'chat' | 'files' | 'ui';
const pageForRoute = (route: Route): Page =>
  route.kind === 'project' || route.kind === 'home'
    ? 'tldr'
    : route.kind === 'chat' || route.kind === 'files' || route.kind === 'ui'
      ? route.kind
      : 'canvas';

/** What a project's Architecture tab can show (prd-deltas §25): its pieces, its deployables, or a view it keeps. */
interface Lens {
  /** The view drawn, or undefined for the deployables (the composition as written). */
  view: string | undefined;
  name: string;
  title: string;
}

/** What the stack looks like on arrival at a route (labels firm up at bundle load). */
const stackForRoute = (route: Route): Altitude[] => {
  const base: Altitude[] = [
    route.kind === 'view' ? { kind: 'composition', view: route.view, label: 'app' } : { kind: 'composition', label: 'app' },
  ];
  if (route.kind === 'view' && route.domain) base.push({ kind: 'composition', view: route.view, domain: route.domain, label: route.domain });
  if (route.kind === 'graph') {
    base.push({
      kind: 'graph',
      path: route.graphPath,
      label: (route.graphPath.split('/').pop() ?? route.graphPath).replace(/\.graph\.ya?ml$/, ''),
    });
  }
  return base;
};

function Workspace({ me }: { me: Me }) {
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [settingsOpen, setSettingsOpen] = useState(false);
  /** The side panels, hidable at any width (prd-deltas §23). */
  const { narrow, panes, setPane, closeOver } = usePanes();
  /** Settings live in the inspector, so opening them shows it. */
  const openSettings = useCallback(() => {
    setSettingsOpen(true);
    setPane('inspector', true);
  }, [setPane]);

  /**
   * PRD 1: the navigation model is TouchDesigner's. Double-click descends into a
   * context and you are then INSIDE it, not looking at it in a panel. The stack is
   * that "inside": the last entry is where you are, and the rest is how you got there.
   */
  const [stack, setStack] = useState<Altitude[]>(() => stackForRoute(BOOT_ROUTE));
  /** The project page on screen; the stack is the canvas's, kept while away from it. */
  const [page, setPage] = useState<Page>(() => pageForRoute(BOOT_ROUTE));
  /** The parts were asked for by name, so arriving does not open a preferred view instead. */
  const partsChosen = useRef(BOOT_ROUTE.kind === 'parts');
  /** The TL;DR and the chat, per project; the TL;DR is written again when a project is opened. */
  const [tldrs, setTldrs] = useState<Record<string, TldrState>>({});
  const [chats, setChats] = useState<Record<string, ChatState>>({});
  /** Selection is plural — shift-drag boxes and shift-clicks accumulate it. */
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedEdgeIds, setSelectedEdgeIds] = useState<string[]>([]);
  /** What Delete is asking about, while it asks. */
  const [confirming, setConfirming] = useState<{ nodes: string[]; edges: string[] } | null>(null);
  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    setSelectedEdgeIds([]);
  }, []);
  const [committing, setCommitting] = useState(false);
  const [commitNote, setCommitNote] = useState<string | null>(null);
  const [diffOpen, setDiffOpen] = useState(false);
  // Bumped when a pre-commit transpile lands emitted files under the open diff,
  // so the panel re-fetches and the review shows exactly what will commit.
  const [applying, setApplying] = useState(false);
  // Source control (owner's rule: no automated git, visible control). gitInfo
  // describes the pinned commit; gitCheck is the remote's answer, present only
  // after the author pressed Check, and dropped whenever the head moves.
  const [gitInfo, setGitInfo] = useState<GitInfo | undefined>(undefined);
  const [gitError, setGitError] = useState<string | null>(null);
  const [gitCheck, setGitCheck] = useState<GitCheck | undefined>(undefined);
  const [checking, setChecking] = useState(false);
  const [gitRevision, setGitRevision] = useState(0);
  const [diffFocus, setDiffFocus] = useState<string | undefined>(undefined);
  const [branchMenuOpen, setBranchMenuOpen] = useState(false);
  const [branchList, setBranchList] = useState<BranchList | undefined>(undefined);
  const [branchesError, setBranchesError] = useState<string | null>(null);
  const [prFormOpen, setPrFormOpen] = useState(false);
  const [gitBusy, setGitBusy] = useState(false);
  const applyingRef = useRef(false);
  // Drift banners the user waved off this session, by orchestration path.
  const [driftDismissed, setDriftDismissed] = useState<Set<string>>(new Set());

  /** The run being watched: its identity, where it stands, and its story so far. */
  const [runPanelOpen, setRunPanelOpen] = useState(false);
  const [activeRun, setActiveRun] = useState<{ id: string; status: RunSummary['status'] } | null>(null);
  const [runEvents, setRunEvents] = useState<RunEvent[]>([]);
  const [runLogOpen, setRunLogOpen] = useState(false);

  /** The app session (docs/app-session.md): what composition Run starts. */
  const [appSession, setAppSession] = useState<AppSessionState>({ phase: 'idle' });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sessionLogOpen, setSessionLogOpen] = useState(false);

  /**
   * The op history, most recent last. In a ref because pushing must not re-render;
   * undoDepth mirrors the length so `enabled(canUndo)` sees changes.
   */
  const undoStack = useRef<UndoEntry[]>([]);
  /** Which graph the active run belongs to, for routing top-level events. */
  const runGraphRef = useRef<string | undefined>(undefined);
  const [undoDepth, setUndoDepth] = useState(0);
  const clearUndo = useCallback(() => {
    undoStack.current = [];
    setUndoDepth(0);
  }, []);

  /** The canvas's own verbs, registered by the Editor when it mounts. */
  const canvasActions = useRef<{ fit: () => void } | null>(null);

  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [activeId, setActiveId] = useState<string | undefined>(() =>
    BOOT_ROUTE.kind === 'home'
      ? (localStorage.getItem(ACTIVE_PROJECT_KEY) ?? undefined)
      : BOOT_ROUTE.projectId,
  );
  /**
   * Civil used to open whichever project was remembered, so a new account arrived
   * inside an example it never chose and an existing one had no way back out. Home
   * is where you land; the remembered project is only a shortcut back to it —
   * unless the address bar names a place, which is a direct link and wins.
   */
  const [atHome, setAtHome] = useState(BOOT_ROUTE.kind === 'home');
  const [keyHelpOpen, setKeyHelpOpen] = useState(false);
  const [addNodeOpen, setAddNodeOpen] = useState(false);
  const [initializing, setInitializing] = useState(false);
  // Generate / Update graph from repo is in flight. Its ref guards re-entry the way
  // applyingRef does: a second press would read the repo and call the model twice.
  const [lifting, setLifting] = useState(false);
  // The last lift's summary, kept under its row in Source control once the toast is
  // dismissed. Per project: opening another clears it.
  const [lastLift, setLastLift] = useState<{ projectId: string; text: string } | null>(null);
  const liftingRef = useRef(false);
  // Where the running lift has got to, read from the server while its POST is pending
  // (null before the first answer). Shown in Source control's lift row and the toast.
  const [liftProgress, setLiftProgress] = useState<LiftRunProgress | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const reloadProjects = useCallback(async () => {
    const list = await fetchProjects();
    setProjects(list);
    return list;
  }, []);

  /** The address bar mirrors the place: home, a project, or a graph inside it. */
  const currentPath = useMemo(() => {
    if (atHome || !activeId) return '/';
    if (page === 'tldr') return routePath({ kind: 'project', projectId: activeId });
    if (page === 'chat') return routePath({ kind: 'chat', projectId: activeId });
    if (page === 'files') return routePath({ kind: 'files', projectId: activeId });
    if (page === 'ui') return routePath({ kind: 'ui', projectId: activeId });
    let graph: string | undefined;
    let place: { view: string; domain?: string } | undefined;
    for (let i = stack.length - 1; i >= 0; i--) {
      const level = stack[i];
      if (level?.kind === 'graph') {
        graph = level.path;
        break;
      }
      if (level?.kind === 'composition' && level.view) {
        const inside = level.domain ?? level.diagram;
        place = { view: level.view, ...(inside ? { domain: inside } : {}) };
        break;
      }
    }
    return routePath(
      graph
        ? { kind: 'graph', projectId: activeId, graphPath: graph }
        : place
          ? { kind: 'view', projectId: activeId, ...place }
          : { kind: 'parts', projectId: activeId },
    );
  }, [atHome, activeId, stack, page]);

  useEffect(() => {
    // Equality is the loop guard: applying a popped state re-derives the same
    // path and pushes nothing.
    if (window.location.pathname !== currentPath) {
      window.history.pushState(null, '', currentPath);
    }
  }, [currentPath]);

  useEffect(() => {
    const onPop = () => {
      const route = parseRoute(window.location.pathname);
      setAtHome(route.kind === 'home');
      if (route.kind !== 'home') setActiveId(route.projectId);
      setPage(pageForRoute(route));
      partsChosen.current = route.kind === 'parts';
      setStack(stackForRoute(route));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);


  /**
   * Re-reads the project after a save or a commit. The bundle is the only place the
   * canvas gets manifests from, so re-fetching it is what makes an edit to app.yaml
   * show up as a changed node rather than requiring a reload.
   */
  const refresh = useCallback(async () => {
    if (!activeId) return;
    try {
      setLoad({ status: 'ready', bundle: await fetchBundle(activeId) });
    } catch { /* leave the previous bundle rendered */ }
  }, [activeId]);

  const commit = useCallback(async (message: string) => {
    if (!activeId) return;
    setCommitting(true);
    setCommitNote(null);
    try {
      const result = await commitProject(activeId, message);
      setCommitNote(`Committed ${result.files} file(s) as ${result.commit.slice(0, 7)}.`);
      // The head moved to the new commit: the old remote answer no longer applies.
      setGitCheck(undefined);
      setGitRevision((n) => n + 1);
      // Undo stops at a commit. Walking back past one would resurrect pre-commit
      // text as a new pending change — an edit war with your own history.
      clearUndo();
      await refresh();
    } catch (error) {
      setCommitNote((error as Error).message);
    } finally {
      setCommitting(false);
    }
  }, [activeId, refresh, clearUndo]);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const list = await fetchProjects(controller.signal);
        setProjects(list);
        // The remembered project may have been removed since; forget it rather than
        // holding a shortcut to something that no longer exists.
        if (activeId && !list.some((p) => p.id === activeId)) {
          setActiveId(undefined);
          localStorage.removeItem(ACTIVE_PROJECT_KEY);
        }
      } catch (error) {
        if (!controller.signal.aborted) setLoad({ status: 'error', message: (error as Error).message });
      }
    })();
    return () => controller.abort();
    // Runs once: later project changes go through setActiveId, which reloads below.
  }, []);

  useEffect(() => {
    if (!activeId) return;
    localStorage.setItem(ACTIVE_PROJECT_KEY, activeId);

    const controller = new AbortController();
    setLoad({ status: 'loading' });
    void fetchBundle(activeId, controller.signal)
      .then((bundle) => {
        setLoad({ status: 'ready', bundle });
        // A deep-linked graph survives the load when the bundle actually has it
        // (its label firms up from the document); anything else lands at the top.
        setStack((current) => {
          // The view survives the load when the bundle still has it. Arriving at a
          // project with no view chosen opens its concept view when it has one: what
          // the application is, before how it is built.
          const asked = current.find((l): l is Extract<Altitude, { kind: 'composition' }> => l.kind === 'composition')?.view;
          const views = bundle.views ?? {};
          const preferred = ['flow', 'architecture', 'concept'].find((v) => views[v]);
          // A Flow tab with no flow view yet stays on Flow, to say so and offer to write it.
          const kept = asked !== undefined && (views[asked] || asked === 'flow') ? asked : undefined;
          const view = asked !== undefined ? kept : partsChosen.current ? undefined : preferred;
          const composition: Altitude = {
            kind: 'composition',
            label: bundle.project.name || 'app',
            ...(view ? { view } : {}),
          };
          const top = current[current.length - 1];
          // A level inside a view survives when its domain still exists: the chain of
          // domains above it is rebuilt from the view, so the breadcrumb reads from the top
          // however the place was reached.
          const loaded = view ? views[view] : undefined;
          // A diagram stepped into survives too (the route names it like a domain).
          const into = top?.kind === 'composition' ? (top.diagram ?? top.domain) : undefined;
          if (into && loaded?.diagrams?.[into] && into !== loaded.view.spec.entry) {
            const doc = loaded.diagrams[into]!.doc;
            return [composition, { kind: 'composition', view: view!, diagram: into, label: doc.metadata.name ?? into }];
          }
          if (top?.kind === 'composition' && top.domain && loaded?.domains[top.domain]) {
            const chain: Altitude[] = [];
            for (let at: string | undefined = top.domain; at && chain.length < 32; at = loaded.domains[at]?.doc.spec.parent) {
              const d = loaded.domains[at];
              if (!d) break;
              chain.unshift({ kind: 'composition', view: view!, domain: at, label: d.doc.metadata.name ?? at });
            }
            return [composition, ...chain];
          }
          if (top?.kind === 'graph' && bundle.graphs[top.path]) {
            const doc = bundle.graphs[top.path]!;
            return [
              composition,
              { kind: 'graph', path: top.path, label: doc.metadata.name || doc.metadata.id },
            ];
          }
          return [composition];
        });
        clearSelection();
        setDiffOpen(false);
        // History is per project; entries name files in the one being left.
        clearUndo();
        // So is the app session — sessionId IS the project id. The probe below
        // re-attaches if this project has one running.
        setAppSession({ phase: 'idle' });
        setPreviewOpen(false);
        setSessionLogOpen(false);
        // Open files belong to the project they were opened in.
        setBrowsing({ files: [] });
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setLoad({ status: 'error', message: (error as Error).message });
        }
      });
    return () => controller.abort();
  }, [activeId]);

  /**
   * Opening a file from the tree is the same code takeover a code node descends into
   * — one file rather than a node's set. Without it, a file no node points at, like
   * CIVIL.md, could not be reached at all.
   */
  /**
   * Stable identity on purpose. An inline arrow is a new value every render, and the
   * effect that reports the active file depends on it — which would run the effect on
   * every render forever.
   */
  /** After a save in either editor: the project re-read, and undo kept honest. */
  const codeSaved = (saved?: string) => {
    if (saved) {
      // The undo stack's snapshots of this file predate the manual save; restoring one
      // would silently destroy it. Those entries die here rather than lying in wait.
      undoStack.current = undoStack.current.filter((entry) => entry.path !== saved);
      setUndoDepth(undoStack.current.length);
    }
    void refresh();
    if (saved) {
      const name = saved.split('/').pop();
      report({ chord: '⌘S', title: 'Save', detail: `${name} saved as a pending change.` });
    }
  };

  const reportActiveFile = useCallback((path: string) => {
    setStack((s) => {
      const top = s[s.length - 1];
      if (top?.kind !== 'code' || top.active === path) return s;
      return [...s.slice(0, -1), { ...top, active: path, label: path.split('/').pop() ?? path }];
    });
  }, []);

  /**
   * `line`, when given, is revealed and highlighted once the file is open — how the
   * inspector's evidence ("path:line") lands on the line a claim rests on.
   */
  const revealSeq = useRef(0);
  /**
   * The File Browser (prd-deltas §25): the files opened from it, or sent to it from
   * anywhere else — the inspector's evidence, the chat, source control. Opening a file
   * goes there; entering a code node from the canvas still takes the canvas over.
   */
  const [browsing, setBrowsing] = useState<{
    files: string[];
    active?: string;
    reveal?: { path: string; line: number; seq: number };
  }>({ files: [] });

  const reportBrowsingFile = useCallback((path: string) => {
    setBrowsing((cur) => (cur.active === path ? cur : { ...cur, active: path }));
  }, []);

  const openFile = useCallback((path: string, line?: number) => {
    setPage('files');
    setBrowsing((cur) => ({
      files: cur.files.includes(path) ? cur.files : [...cur.files, path],
      active: path,
      ...(line ? { reveal: { path, line, seq: ++revealSeq.current } } : {}),
    }));
  }, []);

  const enterProject = useCallback((id: string) => {
    setActiveId(id);
    setAtHome(false);
    // A project opens on its TL;DR, asked for again so what changed since is current (§27).
    setPage('tldr');
    partsChosen.current = false;
    setTldrs(({ [id]: _stale, ...rest }) => rest);
  }, []);

  const goHome = useCallback(() => {
    setAtHome(true);
    setPickerOpen(false);
  }, []);

  const descend = useCallback((altitude: Altitude) => {
    clearSelection();
    setStack((s) => [...s, altitude]);
  }, [clearSelection]);

  const ascend = useCallback(() => {
    clearSelection();
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, [clearSelection]);

  const ascendTo = useCallback((index: number) => {
    clearSelection();
    setStack((s) => s.slice(0, index + 1));
  }, [clearSelection]);

  /** Another way of looking at the composition (§21): its top, in that view. */
  const switchView = useCallback((view: string | undefined) => {
    clearSelection();
    setPage('canvas');
    partsChosen.current = view === undefined;
    setStack((s) => {
      const root = s[0];
      const label = root?.label ?? 'app';
      return [view ? { kind: 'composition', label, view } : { kind: 'composition', label }];
    });
  }, [clearSelection]);

  const bundle = load.status === 'ready' ? load.bundle : undefined;
  const current = stack[stack.length - 1]!;

  // The flow's altitudes (docs/altitude.md): Story above Product above Mechanism, and
  // the one on screen. A flow written before altitudes has none, and no switch.
  const flowLoaded = bundle?.views?.['flow'];
  const flowAltitudes = useMemo(() => altitudesOf(flowLoaded), [flowLoaded]);
  const diagramHere = current.kind === 'composition' && current.view === 'flow' ? (current.diagram ?? flowLoaded?.view.spec.entry) : undefined;
  const altitudeHere = diagramHere && flowAltitudes.length > 1 ? altitudeOf(flowLoaded, diagramHere) : undefined;

  /**
   * To another altitude, at its entry, carrying `ids` across as a footprint: what they
   * are said as up there, or what they cover down there. Says what it found.
   */
  const toAltitude = useCallback(
    (to: FlowAltitude, ids: readonly string[]): string | undefined => {
      const target = flowAltitudes.find((a) => a.altitude === to);
      if (!flowLoaded || !diagramHere || !altitudeHere || altitudeHere === to || !target) return undefined;
      const found = footprint(flowLoaded, diagramHere, ids, to);
      const label = bundle?.project.name || 'app';
      const entry = flowLoaded.diagrams?.[target.entry]?.doc;
      setPage('canvas');
      partsChosen.current = false;
      setStack([
        { kind: 'composition', label, view: 'flow' },
        ...(target.entry !== flowLoaded.view.spec.entry
          ? [{ kind: 'composition' as const, label: entry?.metadata.name ?? target.entry, view: 'flow', diagram: target.entry }]
          : []),
      ]);
      setSelectedEdgeIds([]);
      setSelectedIds(found.onEntry);
      const name = ALTITUDE_NAMES[to];
      if (!ids.length) return `${name}.`;
      if (!found.all.length) return `${name}: nothing here describes what was selected.`;
      const names = found.all.slice(0, 5).map((p) => p.node.name);
      return `${name}: ${names.join(', ')}${found.all.length > 5 ? `, and ${found.all.length - 5} more` : ''}.`;
    },
    [flowAltitudes, flowLoaded, diagramHere, altitudeHere, bundle?.project.name],
  );
  /** One altitude up (-1) or down (+1) from the one on screen. */
  const stepAltitude = useCallback(
    (by: -1 | 1, ids: readonly string[]): string | undefined => {
      const at = flowAltitudes.findIndex((a) => a.altitude === altitudeHere);
      const next = at < 0 ? undefined : flowAltitudes[at + by];
      return next ? toAltitude(next.altitude, ids) : undefined;
    },
    [flowAltitudes, altitudeHere, toAltitude],
  );

  // On a narrow screen a panel covers the canvas: moving somewhere — a file opened
  // from the tree, a node entered — closes it, so you land looking at where you went.
  useEffect(() => {
    closeOver();
  }, [stack, page, closeOver]);

  /** The project's TL;DR: the kept one with what changed since, or written now; `write` writes it again. */
  const loadTldr = useCallback((projectId: string, write = false) => {
    setTldrs((all) => ({ ...all, [projectId]: { status: 'loading', ...(write ? { writing: true } : {}) } }));
    void fetchTldr(projectId, write)
      .then((tldr) =>
        setTldrs((all) => ({
          ...all,
          [projectId]: { status: 'ready', tldr, at: tldr.writtenAt ? Date.parse(tldr.writtenAt) : Date.now() },
        })),
      )
      .catch((error: unknown) =>
        setTldrs((all) => ({ ...all, [projectId]: { status: 'error', message: (error as Error).message } })),
      );
  }, []);

  useEffect(() => {
    if (!atHome && page === 'tldr' && activeId && !tldrs[activeId]) loadTldr(activeId);
  }, [atHome, page, activeId, tldrs, loadTldr]);

  useEffect(() => {
    if (atHome || page !== 'chat' || !activeId || chats[activeId]) return;
    setChats((all) => ({ ...all, [activeId]: { status: 'loading' } }));
    void fetchChat(activeId)
      .then((chat) => setChats((all) => ({ ...all, [activeId]: { status: 'ready', ...chat } })))
      .catch((error: unknown) =>
        setChats((all) => ({ ...all, [activeId]: { status: 'error', message: (error as Error).message } })),
      );
  }, [atHome, page, activeId, chats]);

  const askQuestion = useCallback((projectId: string, question: string) => {
    let chatId: string | null = null;
    setChats((all) => {
      const at = all[projectId];
      if (at?.status !== 'ready') return all;
      chatId = at.chatId;
      const { error: _cleared, ...rest } = at;
      return { ...all, [projectId]: { ...rest, asking: question } };
    });
    void askChat(projectId, question, chatId)
      .then(({ chatId: id, answer }) =>
        setChats((all) => {
          const at = all[projectId];
          if (at?.status !== 'ready') return all;
          const { asking: _done, ...rest } = at;
          return {
            ...all,
            [projectId]: { ...rest, chatId: id, messages: [...at.messages, { role: 'user', content: question }, answer] },
          };
        }),
      )
      .catch((error: unknown) =>
        setChats((all) => {
          const at = all[projectId];
          if (at?.status !== 'ready') return all;
          const { asking: _failed, ...rest } = at;
          return { ...all, [projectId]: { ...rest, error: (error as Error).message } };
        }),
      );
  }, []);

  const newConversation = useCallback(async (projectId: string): Promise<string> => {
    const chat = await startNewChat(projectId);
    setChats((all) => ({ ...all, [projectId]: { status: 'ready', chatId: chat.chatId, messages: [] } }));
    return chat.effect;
  }, []);

  /**
   * To a node of Flow or Architecture, selected (§25): its view, stepped into the
   * diagram that holds it when that is not the view's entry.
   */
  const goToNode = useCallback((view: string, diagram: string, node: string) => {
    const loaded = bundle?.views?.[view];
    if (!loaded) return;
    clearSelection();
    setPage('canvas');
    partsChosen.current = false;
    const label = bundle?.project.name || 'app';
    const inside = diagram !== loaded.view.spec.entry && loaded.diagrams?.[diagram];
    setStack([
      { kind: 'composition', label, view },
      ...(inside ? [{ kind: 'composition' as const, label: inside.doc.metadata.name ?? diagram, view, diagram }] : []),
    ]);
    setSelectedIds([node]);
  }, [bundle, clearSelection]);

  /** A part named in the chat, shown where it is written: the parts view, selected. */
  const openPart = useCallback((id: string) => {
    switchView(undefined);
    setSelectedIds([id]);
  }, [switchView]);

  const partIds = useMemo(() => new Set((bundle?.composition?.spec.nodes ?? []).map((n) => n.id)), [bundle?.composition]);
  /** The UI tab's frontends: the composition's clients (docs/ui-tab.md §1). */
  const uiFrontends = useMemo(
    () => (bundle?.composition?.spec.nodes ?? []).filter((n) => n.type === 'client').map((n) => ({ id: n.id, name: n.id })),
    [bundle?.composition],
  );
  const uiRef = useRef<UiTabHandle>(null);
  const [uiTab, setUiTab] = useState<UiTabState>({ hasRequest: false, hasSnapshot: false, hasSelectedMark: false, mode: 'annotate' });
  const fileSet = useMemo(() => new Set(bundle?.files ?? []), [bundle?.files]);

  /**
   * The tabs (prd-deltas §25): what the application is in a sentence, what it does, what
   * it is made of, questions about it, and its files. Flow and Architecture each cover
   * the whole application; Parts — the deployables Generate app reads — is a lens of
   * Architecture rather than a tab, beside any grouping view the project keeps.
   */
  const viewTabs = useMemo(
    () => [
      { key: 'tldr', name: 'TL;DR', title: 'What the application is, in at most four sentences (T)' },
      { key: 'flow', name: 'Flow', title: 'What the application does: the mental model to start in' },
      { key: 'architecture', name: 'Architecture', title: 'What the application is made of: its pieces, framework opinions, and where the code lives' },
      { key: 'ui', name: 'UI', title: 'Mark up the running application: snapshots, notes and drawings (I)' },
      { key: 'chat', name: 'Chat', title: 'Ask about the application (C)' },
      { key: 'files', name: 'File Browser', title: 'The repository\'s files, and the code (E)' },
    ],
    [],
  );
  const lenses = useMemo((): Lens[] => {
    const views = bundle?.views ?? {};
    return [
      ...(views['architecture']
        ? [{ view: 'architecture', name: 'Pieces', title: 'What the application is made of, with the decisions behind each piece' }]
        : []),
      { view: undefined, name: 'Deployables', title: 'The parts as civil/app.yaml writes them: what is built and deployed. Edit here; Generate app reads these.' },
      ...viewOrder(Object.keys(views))
        .filter((id) => id !== 'flow' && id !== 'architecture')
        .map((id) => ({
          view: id,
          name: views[id]!.view.metadata.name ?? id,
          title: views[id]!.view.spec.description ?? `The ${views[id]!.view.metadata.name ?? id} view`,
        })),
    ];
  }, [bundle?.views]);
  const rootView = stack[0]?.kind === 'composition' ? stack[0].view : undefined;
  const activeTab = page !== 'canvas' ? page : rootView === 'flow' ? 'flow' : 'architecture';
  /** The lens Architecture was last left on, so coming back finds it as it was. */
  const archLens = useRef<string | undefined | null>(null);
  if (activeTab === 'architecture') archLens.current = rootView;
  const goToTab = useCallback((key: string) => {
    if (key === 'tldr' || key === 'chat' || key === 'files' || key === 'ui') {
      // The inspector speaks for the canvas; away from it, nothing is selected.
      clearSelection();
      setPage(key);
    } else if (key === 'flow') {
      switchView('flow');
    } else {
      const views = bundle?.views ?? {};
      const last = archLens.current;
      switchView(last !== null && (last === undefined || views[last]) ? last : views['architecture'] ? 'architecture' : undefined);
    }
  }, [clearSelection, switchView, bundle?.views]);
  const pendingCount = bundle?.pending.length ?? 0;
  const canCommit = bundle?.project.sourceKind === 'github';
  // Generated files, as a Set for the editor's read-only check. Recomputed when the
  // bundle refreshes, so a fresh transpile's new outputs become read-only at once.
  const maintainedSet = useMemo(() => new Set(bundle?.maintained ?? []), [bundle?.maintained]);
  // Where new civil documents go: the directory the composition already lives in
  // ('civil/' once migrated, '' for a legacy root project) — delta 19.
  const docDir = useMemo(() => {
    const cp = bundle?.compositionPath ?? '';
    const slash = cp.lastIndexOf('/');
    return slash >= 0 ? cp.slice(0, slash + 1) : '';
  }, [bundle?.compositionPath]);

  const fatalCount = useMemo(
    () => bundle?.diagnostics.filter((d) => d.severity === 'error').length ?? 0,
    [bundle],
  );
  const runBlockedCount = useMemo(
    () => bundle?.diagnostics.filter((d) => d.severity === 'run-blocking').length ?? 0,
    [bundle],
  );

  // docs/app-session.md supersedes PRD 4's "Run has no meaning on the composition
  // canvas": composition Run runs the APP — the session — while graph Run keeps the
  // module loop untouched. PRD 6.4 still holds on a graph: a flow cycle blocks Run
  // without failing the save, and validation errors block both altitudes.
  const sessionReady =
    current.kind === 'composition' && load.status === 'ready' && fatalCount === 0 && bundle?.generation?.state !== 'unsupported';
  const sessionActive = appSession.phase === 'starting' || appSession.phase === 'live';
  // A project whose own TypeScript is the implementation has nothing Civil can run:
  // Run transpiles to Python first. Disabled, with the reason as its title, rather
  // than a red 409 and a "Try again" that can never succeed.
  const generationUnsupported = bundle?.generation?.state === 'unsupported';
  const runDisabled =
    generationUnsupported || (current.kind === 'graph' ? runBlockedCount > 0 || fatalCount > 0 : !sessionReady);

  /**
   * The keyboard dispatches into the command registry, and an agent will dispatch
   * into the same one (CLAUDE.md). Each handler returns what it did in words, which
   * is what the toast shows — and what an agent transcript will show later.
   */
  const commandContext: CommandContext = {
    // The diff panel is a location, not an overlay: while it is open, canvas
    // commands must not fire underneath it, or the diff on screen stops being
    // the diff that commits.
    where: atHome
      ? 'home'
      : diffOpen
        ? 'diff'
        : page !== 'canvas'
          ? page
          : current.kind === 'code'
            ? 'code'
            : 'canvas',
    hasSelection: selectedIds.length > 0 || selectedEdgeIds.length > 0,
    pendingCount,
    canCommit,
    canUndo: undoDepth > 0,
    canRun: current.kind === 'graph' && !runDisabled,
    runActive: activeRun?.status === 'queued' || activeRun?.status === 'running',
    canSession: sessionReady,
    sessionActive,
    // Away from the canvas there is nothing to ascend: Escape must not move it unseen.
    depth: page === 'canvas' ? stack.length - 1 : 0,
    // Decides whether reading the repo is "Generate" or "Update" (project.liftRepo).
    hasComposition: bundle?.composition !== undefined,
    // A project the lift would refuse is not offered it (Source control says why).
    liftRefused: !!bundle?.lift?.refusal,
    viewCount: Object.keys(bundle?.views ?? {}).length,
    inView: current.kind === 'composition' && !!current.view,
    isAdmin: me.admin,
    inArchitecture: activeTab === 'architecture',
    ...(altitudeHere ? { altitude: altitudeHere, altitudes: flowAltitudes.map((a) => a.altitude) } : {}),
    treeShown: panes.tree,
    inspectorShown: panes.inspector,
    uiHasSnapshot: page === 'ui' && uiTab.hasSnapshot,
    uiHasSelectedMark: page === 'ui' && uiTab.hasSelectedMark,
  };
  // One name for the verb everywhere it appears — buttons, title attributes, toasts.
  const liftTitle = titleOf(commandById('project.liftRepo'), commandContext);
  const { effect, report, run } = useCommands(
    commandContext,
    {
      'nav.home': () => {
        goHome();
        return 'Showing all projects.';
      },
      'project.switch': () => {
        setPickerOpen((v) => !v);
        return 'Project picker.';
      },
      'nav.ascend': () => {
        if (stack.length <= 1) return undefined;
        const leaving = stack[stack.length - 1]!;
        ascend();
        return `Left ${leaving.label}.`;
      },
      'nav.descend': () => {
        // The same question the double-click asks, asked of the one selected node.
        if (current.kind === 'code' || !bundle) return undefined;
        if (selectedIds.length !== 1 || selectedEdgeIds.length > 0) {
          return selectedEdgeIds.length > 0 ? 'An edge has no interior.' : undefined;
        }
        const context = {
          graphs: bundle.graphs,
          agents: bundle.agents,
          files: bundle.files,
          contracts: bundle.contracts,
          ...(bundle.lift ? { repoUnits: bundle.lift.units } : {}),
        };
        const shown = diagramShown(bundle, current);
        const flow =
          shown && current.kind === 'composition'
            ? diagramToFlow(shown.doc, shown.path, current.view!, bundle.diagnostics, bundle.views?.[current.view!]?.diagrams, codeSources(bundle, current.view))
            : current.kind === 'composition'
            ? bundle.composition &&
              compositionToFlow(
                bundle.composition,
                bundle.compositionPath,
                bundle.diagnostics,
                context,
                current.view ? bundle.views?.[current.view] : undefined,
                current.domain,
              )
            : bundle.graphs[current.path] &&
              graphToFlow(bundle.graphs[current.path]!, current.path, bundle.diagnostics, context);
        const flowNode = flow?.nodes.find((n) => n.id === selectedIds[0]);
        const target = flowNode ? descentTarget(flowNode.data as NodeData) : undefined;
        // A Story moment is not a door: going down from it is the altitude below.
        if (!target && altitudeHere === 'story') return stepAltitude(1, selectedIds);
        if (!target) return `“${selectedIds[0]}” is a leaf — nothing to enter.`;
        descend(target);
        return `Entered ${target.label}.`;
      },
      'view.next': () => {
        const at = viewTabs.findIndex((tab) => tab.key === activeTab);
        const next = viewTabs[(at + 1) % viewTabs.length]!;
        goToTab(next.key);
        return `Showing ${next.key === 'tldr' ? 'the TL;DR' : next.key === 'files' ? 'the files' : `the ${next.name.toLowerCase()}`}.`;
      },
      'view.tldr': () => {
        goToTab('tldr');
        return null;
      },
      'view.chat': () => {
        goToTab('chat');
        return null;
      },
      'view.files': () => {
        goToTab('files');
        return null;
      },
      'view.ui': () => {
        goToTab('ui');
        return null;
      },
      'ui.request.new': () => {
        void uiRef.current?.newRequest();
        return null;
      },
      'ui.snapshot.add': () => uiRef.current?.chooseScreenshot(),
      'ui.capture': () => {
        void uiRef.current?.capture();
        return null;
      },
      'ui.request.match': () => {
        void uiRef.current?.match();
        return null;
      },
      'ui.request.interpret': () => {
        void uiRef.current?.interpret();
        return null;
      },
      'ui.request.copy': () => {
        void uiRef.current?.copyPrompt();
        return null;
      },
      'ui.request.download': () => {
        void uiRef.current?.download();
        return null;
      },
      'ui.mode.view': () => uiRef.current?.setMode('view'),
      'ui.mode.annotate': () => uiRef.current?.setMode('annotate'),
      'ui.mode.draw': () => uiRef.current?.setMode('draw'),
      'ui.mark.remove': () => {
        void uiRef.current?.removeSelectedMark().then((effect) => effect && report({ title: 'Remove mark', detail: effect }));
        return null;
      },
      'view.lens': () => {
        const at = lenses.findIndex((lens) => lens.view === rootView);
        const next = lenses[(at + 1) % lenses.length]!;
        switchView(next.view);
        return `Architecture: ${next.name.toLowerCase()}.`;
      },
      'altitude.up': () => stepAltitude(-1, selectedIds),
      'altitude.down': () => stepAltitude(1, selectedIds),
      'altitude.story': () => toAltitude('story', selectedIds),
      'altitude.product': () => toAltitude('product', selectedIds),
      'altitude.mechanism': () => toAltitude('mechanism', selectedIds),
      'tldr.regenerate': () => {
        if (!activeId) return undefined;
        loadTldr(activeId, true);
        return 'Writing the TL;DR again from the documents as they are now.';
      },
      'chat.new': () => {
        if (!activeId) return undefined;
        void newConversation(activeId).then((effect) => report({ title: 'New conversation', detail: effect }));
        return null;
      },
      'canvas.fit': () => {
        if (!canvasActions.current) return undefined;
        canvasActions.current.fit();
        // The motion is its own feedback.
        return null;
      },
      'canvas.clearSelection': () => {
        if (selectedIds.length === 0 && selectedEdgeIds.length === 0) return undefined;
        clearSelection();
        return 'Selection cleared.';
      },
      'canvas.delete': () => {
        // Deleting destroys information, so it asks first. The modal is the
        // feedback, so the command itself is silent.
        if (selectedIds.length === 0 && selectedEdgeIds.length === 0) return undefined;
        // A view groups parts; it never removes one (§21). Lines are the composition's,
        // so those may go from here.
        if (bundle && diagramShown(bundle, current)) {
          return 'This view is a reading of the code; Generate or Update graph from repo draws it. Nothing here is removed by hand.';
        }
        if (commandContext.inView && selectedIds.length > 0) {
          return 'A view only groups parts. Remove a part in the parts view (press v until it shows).';
        }
        setConfirming({ nodes: selectedIds, edges: selectedEdgeIds });
        return null;
      },
      'edit.undo': () => {
        const entry = undoStack.current[undoStack.current.length - 1];
        if (!entry) return undefined;
        void undoLast();
        return `Undoing: ${entry.summary}`;
      },
      'run.start': () => {
        setRunPanelOpen(true);
        // The input panel is the next thing on screen; it speaks for itself.
        return null;
      },
      'run.cancel': () => {
        if (!activeRun || !activeId) return undefined;
        void cancelRun(activeId, activeRun.id).catch(() => undefined);
        return 'Cancellation requested — the run stops at the next node boundary.';
      },
      'session.start': () => {
        void beginSession();
        return sessionActive
          ? 'Attached — the app session is already up.'
          : 'Starting the app session…';
      },
      'session.stop': () => {
        if (!sessionActive) return undefined;
        void endSession();
        return 'Stopping the app session.';
      },
      'project.diff': () => {
        if (pendingCount === 0) return undefined;
        // Same review as the commit path: transpile first so the emitted code is in
        // the diff, since this panel carries the commit control too. reviewBeforeCommit
        // no-ops the transpile for a non-committable project.
        reviewBeforeCommit();
        return 'Every pending change, as a diff, before it commits.';
      },
      'project.sync': () => {
        if (!canCommit) return undefined;
        void doSync();
        return 'Checking the repository…';
      },
      'git.branch': () => {
        if (!canCommit) return undefined;
        setBranchMenuOpen((open) => !open);
        return 'Branches';
      },
      'git.pullRequest': () => {
        if (!canCommit) return undefined;
        if (!gitInfo?.baseBranch) return 'You are on the default branch — create a branch to propose changes from.';
        setPrFormOpen((open) => !open);
        return 'Pull request';
      },
      'git.check': () => {
        if (!canCommit) return undefined;
        void doCheck();
        return 'Asking GitHub…';
      },
      'project.apply': () => {
        // A TypeScript project's own code is the implementation (registry.ts): say so
        // here too, so the keyboard and an agent hear what the button area shows.
        if (bundle?.generation?.state === 'unsupported') {
          report({ title: 'Apply changes', detail: GENERATION_UNSUPPORTED, refused: true });
          return null;
        }
        void doApply();
        return 'Transpiling the civil documents…';
      },
      'project.liftRepo': () => {
        if (!activeId || liftingRef.current) return undefined;
        void doLiftRepo();
        // Stays, and follows the run's progress (live): reading takes minutes.
        return {
          detail: 'Reading the repository — proposed documents arrive as pending changes to review.',
          sticky: true,
          live: 'lift',
        };
      },
      'project.settings': () => {
        if (settingsOpen && panes.inspector) {
          setSettingsOpen(false);
          return 'Settings closed.';
        }
        openSettings();
        return 'Settings.';
      },
      // The panel moving is its own feedback; a toast over it would hide what changed.
      'pane.tree': () => {
        setPane('tree');
        return null;
      },
      'pane.inspector': () => {
        setPane('inspector');
        return null;
      },
      'access.manage': () => {
        if (!me.admin) return undefined;
        window.location.assign('/admin');
        return 'Opening access management.';
      },
      'prompts.manage': () => {
        if (!me.admin) return undefined;
        window.location.assign('/admin/prompts');
        return 'Opening the prompts.';
      },
      'help.keys': () => {
        setKeyHelpOpen((v) => !v);
        return 'Every shortcut and what it does.';
      },
      'node.add': () => {
        if (current.kind === 'code') return undefined;
        // A repository with no manifests has nothing to add a node to. Saying that is
        // more useful than letting the op fail with "app.yaml could not be read",
        // which is true and tells you nothing about what to do next.
        if (load.status === 'ready' && !load.bundle.composition) {
          report({
            title: 'Add node',
            detail: `${load.bundle.compositionPath} does not exist yet — this repository is not a Civil project.`,
            refused: true,
          });
          return undefined;
        }
        setAddNodeOpen(true);
        return 'Choose a node type.';
      },
      'project.commit': () => {
        if (pendingCount === 0) return undefined;
        setPickerOpen(false);
        // Committing needs a message and deserves the diff in front of it, so the
        // shortcut opens the review panel rather than committing silently — PRD 7
        // makes commits explicit and gives the indicator a diff preview. Auto, then
        // review: the emitted code lands in that diff too, not just the intent edits.
        reviewBeforeCommit();
        return `${pendingCount} pending change${pendingCount === 1 ? '' : 's'}. Review, add a message, commit.`;
      },
    },
  );

  /** What the UI tab did, as every command says it: in the toast (CLAUDE.md, agent first). */
  const uiEffect = useCallback(
    (title: string, detail: string, failed?: boolean) => report({ title, detail, ...(failed ? { refused: true } : {}) }),
    [report],
  );

  const runTitle = generationUnsupported
    ? `Run isn't available: ${GENERATION_UNSUPPORTED}`
    : current.kind === 'composition'
      ? fatalCount > 0
        ? `${fatalCount} validation error${fatalCount === 1 ? '' : 's'}`
        : sessionActive
          ? 'The app session is up — Run reopens the preview'
          : 'Run the app: transpile, start every process, open the preview'
      : current.kind === 'code'
        ? 'Run belongs to a canvas, not to a file'
        : fatalCount > 0
        ? `${fatalCount} validation error${fatalCount === 1 ? '' : 's'}`
        : runBlockedCount > 0
          ? 'A flow cycle blocks Run until it is broken'
          : 'Run this graph';

  /**
   * PRD 7.1: the client posts ops and the server applies them. The same call is what
   * an agent will make, which is why the placement and the id are decided here rather
   * than inside a component that only the keyboard can reach.
   */
  const initialize = useCallback(async () => {
    if (!activeId) return;
    setInitializing(true);
    try {
      const { files, summary } = await initializeProject(activeId);
      await refresh();
      report({ title: 'Add Civil', detail: summary });
      // Land in CIVIL.md: the scaffold's whole point is that you describe the project,
      // and an empty canvas does not invite that.
      if (files.includes('CIVIL.md')) openFile('CIVIL.md');
    } catch (error) {
      report({ title: 'Add Civil', detail: (error as Error).message, refused: true });
    } finally {
      setInitializing(false);
    }
  }, [activeId, refresh, report, openFile]);

  // Asked only from Check (button or G): the one call that reads the remote's present.
  const doCheck = useCallback(async () => {
    if (!activeId) return;
    setChecking(true);
    try {
      const result = await checkGit(activeId);
      setGitCheck(result);
      report({
        title: 'Check GitHub',
        detail: result.diverged
          ? 'The branch history was rewritten on GitHub — sync to pick it up.'
          : result.behind
            ? `${result.behind} new commit${result.behind === 1 ? '' : 's'} on GitHub. Sync when ready.`
            : result.behind === null
              ? 'GitHub has commits you have not synced.'
              : 'Up to date with GitHub.',
      });
    } catch (error) {
      report({ title: 'Check GitHub', detail: (error as Error).message, refused: true });
    } finally {
      setChecking(false);
    }
  }, [activeId, report]);

  const doRevert = useCallback(
    async (path: string) => {
      if (!activeId) return;
      try {
        await revertFile(activeId, path);
        // An undo entry for this file would restore text over the revert.
        undoStack.current = undoStack.current.filter((entry) => entry.path !== path);
        setUndoDepth(undoStack.current.length);
        await refresh();
        report({ title: 'Discard change', detail: `${path} is back to the committed version.` });
      } catch (error) {
        report({ title: 'Discard change', detail: (error as Error).message, refused: true });
      }
    },
    [activeId, refresh, report],
  );

  const doResolve = useCallback(
    async (path: string, side: 'mine' | 'theirs') => {
      if (!activeId) return;
      try {
        const summary = await resolveConflict(activeId, path, side);
        if (side === 'theirs') {
          undoStack.current = undoStack.current.filter((entry) => entry.path !== path);
          setUndoDepth(undoStack.current.length);
        }
        await refresh();
        report({ title: side === 'mine' ? 'Keep mine' : 'Take theirs', detail: summary });
      } catch (error) {
        report({ title: 'Resolve', detail: (error as Error).message, refused: true });
      }
    },
    [activeId, refresh, report],
  );

  // What the panel says about the pinned commit. Re-read when the project changes and
  // after a commit or sync moves the head — never on a timer.
  useEffect(() => {
    setGitInfo(undefined);
    setGitError(null);
    setGitCheck(undefined);
    if (!activeId || bundle?.project.id !== activeId || bundle.project.sourceKind !== 'github') return;
    const controller = new AbortController();
    fetchGit(activeId, controller.signal)
      .then(setGitInfo)
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setGitError((error as Error).message);
      });
    return () => controller.abort();
  }, [activeId, bundle?.project.id, bundle?.project.sourceKind, gitRevision]);

  // The branch list is GitHub's, so it is read when the author opens the menu — not
  // before, and not again until they reopen it.
  useEffect(() => {
    if (!branchMenuOpen || !activeId) return;
    setBranchList(undefined);
    setBranchesError(null);
    const controller = new AbortController();
    fetchBranches(activeId)
      .then((list) => !controller.signal.aborted && setBranchList(list))
      .catch((error: unknown) => !controller.signal.aborted && setBranchesError((error as Error).message));
    return () => controller.abort();
  }, [branchMenuOpen, activeId]);

  /**
   * One shape for every action that moves the author to another branch or opens a
   * pull request: run it, then let go of everything tied to where they were — the
   * undo history, the last Check, the open menus — and re-read.
   */
  const runGitAction = useCallback(
    async (title: string, action: (projectId: string) => Promise<string>) => {
      if (!activeId || gitBusy) return;
      setGitBusy(true);
      try {
        const summary = await action(activeId);
        setBranchMenuOpen(false);
        setPrFormOpen(false);
        clearUndo();
        setGitCheck(undefined);
        setGitRevision((n) => n + 1);
        await refresh();
        report({ title, detail: summary });
      } catch (error) {
        report({ title, detail: (error as Error).message, refused: true });
      } finally {
        setGitBusy(false);
      }
    },
    [activeId, gitBusy, clearUndo, refresh, report],
  );

  const doSync = useCallback(async () => {
    if (!activeId) return;
    try {
      const { summary, moved } = await syncProject(activeId);
      setGitCheck(undefined);
      setGitRevision((n) => n + 1);
      if (moved) {
        // The head this history was recorded against is gone.
        clearUndo();
        await refresh();
      }
      report({ title: 'Sync', detail: summary });
    } catch (error) {
      report({ title: 'Sync', detail: (error as Error).message, refused: true });
    }
  }, [activeId, refresh, report, clearUndo]);

  /**
   * The emitted files arrive as pending changes, so the refresh is what makes them
   * appear in the tree and the commit indicator — the same beat every other
   * mutation follows.
   */
  const doApply = useCallback(async () => {
    if (!activeId || applyingRef.current) return;
    // A ref, not state, guards re-entry: two presses inside one render would both
    // see applying=false and start two model calls.
    applyingRef.current = true;
    setApplying(true);
    const started = performance.now();
    try {
      const { files, cached, patternsRefreshed } = await transpileProject(activeId);
      await refresh();
      const seconds = Math.round((performance.now() - started) / 1000);
      const count = `${files.length} file${files.length === 1 ? '' : 's'}`;
      report({
        title: 'Apply changes',
        detail: cached
          ? `Already generated for this sketch — ${count} restored from cache.`
          : `Generated ${count} in ${seconds}s.${patternsRefreshed ? ' Code patterns re-read.' : ''}`,
      });
    } catch (error) {
      report({ title: 'Apply changes', detail: (error as Error).message, refused: true });
    } finally {
      applyingRef.current = false;
      setApplying(false);
    }
  }, [activeId, refresh, report]);

  /**
   * Generate / Update graph from repo (docs/lift-repo.md): the server reads the
   * repository's code and lands proposed civil/ documents as pending changes. The
   * refresh shows them in the tree and the commit count; the canvas opens on the
   * composition so the result is seen at once, and the diff is one click away —
   * nothing reaches the repository until the author commits it.
   */
  const doLiftRepo = useCallback(async () => {
    if (!activeId || liftingRef.current) return;
    liftingRef.current = true;
    setLifting(true);
    setLiftProgress(null);
    const title = liftTitle;
    // The client names the run so its progress can be read while the POST is pending;
    // polling stops when the POST settles, which is the answer that counts.
    const runId = crypto.randomUUID();
    const stopWatching = watchLiftRun(activeId, runId, setLiftProgress);
    try {
      const result = await liftRepo(activeId, runId);
      stopWatching();
      await refresh();
      // Back to the top: the composition is what was just written or updated.
      setDiffOpen(false);
      ascendTo(0);
      const extras = [
        liftAnalysisNote(result.analysis),
        liftInterpretationNote(result.interpretation),
        result.note,
        result.diagnostics.length > 0
          ? `${result.diagnostics.length} thing${result.diagnostics.length === 1 ? '' : 's'} could not be resolved — see civil/architecture.md.`
          : null,
      ].filter(Boolean);
      const detail = [result.summary, ...extras].join('\n');
      // A result to read, after an operation that took a while: it stays until
      // dismissed, and stays in Source control after that.
      report({ title, detail, sticky: true });
      setLastLift({ projectId: activeId, text: detail });
      // Show the whole composition just written, not whatever part of it fits.
      window.setTimeout(() => canvasActions.current?.fit(), 300);
    } catch (error) {
      report({ title, detail: (error as Error).message, refused: true, sticky: true });
    } finally {
      stopWatching();
      liftingRef.current = false;
      setLifting(false);
      setLiftProgress(null);
    }
  }, [activeId, liftTitle, refresh, report, ascendTo]);

  /**
   * The open graph's orchestration, when Civil emitted it and it was then changed
   * outside Civil — the drift lift reconciles (docs/lift.md). Naming convention:
   * graphs/X.graph.yaml is emitted to graphs/X.py.
   */
  const driftedOrchestration = useMemo(() => {
    if (current.kind !== 'graph' || !bundle) return undefined;
    // The transpiler chooses the orchestration's path, so match a drifted file to
    // this graph by basename stem (classify.graph.yaml <-> .../classify.py) rather
    // than a same-directory guess (docs/lift.md).
    const stem = current.path.split('/').pop()?.replace(/\.graph\.ya?ml$/, '');
    const orchestration = bundle.drifted.find((d) => d.split('/').pop() === `${stem}.py`);
    if (!orchestration || driftDismissed.has(orchestration)) return undefined;
    return orchestration;
  }, [current, bundle, driftDismissed]);

  const doLift = useCallback(async () => {
    if (!activeId || current.kind !== 'graph') return;
    const graphPath = current.path;
    try {
      const result = await liftGraph(activeId, graphPath);
      if (result.unliftable) {
        report({
          title: 'Lift',
          detail: `Can't update the graph from this code: ${result.reason ?? 'unliftable'}. Regenerate to reconcile.`,
          refused: true,
        });
        return;
      }
      await refresh();
      report({
        title: 'Lift',
        detail:
          result.added + result.removed === 0
            ? 'The graph already matches the code.'
            : `Graph updated from the code: +${result.added} / -${result.removed} edge(s).`,
      });
    } catch (error) {
      report({ title: 'Lift', detail: (error as Error).message, refused: true });
    }
  }, [activeId, current, refresh, report]);

  const [migrateDismissed, setMigrateDismissed] = useState(false);
  /**
   * A legacy project keeps its documents at the repo root; migrating moves them into
   * civil/ (delta 19). A project is legacy when it has a composition but that
   * composition sits outside civil/ — docDir is then ''. Examples migrate in-repo,
   * so this only ever prompts a real github project.
   */
  const isLegacy = bundle?.composition !== undefined && docDir === '' && !migrateDismissed;

  const doMigrate = useCallback(async () => {
    if (!activeId) return;
    try {
      const { moved, summary, warnings } = await migrateProject(activeId);
      await refresh();
      const detail = summary || `Moved ${moved.length} document(s) into civil/.`;
      report({ title: 'Migrate', detail: warnings.length ? `${detail}\n${warnings.join('\n')}` : detail });
    } catch (error) {
      report({ title: 'Migrate', detail: (error as Error).message, refused: true });
    }
  }, [activeId, refresh, report]);

  /**
   * Auto, then review (docs/transpilation.md): opening the commit review lands a
   * fresh transpile in the diff, so intent changes and the emitted code they cause
   * are seen together before confirming. The server re-transpiles at commit (the
   * memo makes it identical), so this is a faithful preview of exactly what lands.
   * Quiet — no success toast, because it fires whenever the review opens; only a
   * failure is worth interrupting for. A non-committable project just shows pending.
   */
  // Opening the review generates nothing (generation is explicit — Apply changes).
  // The panel refuses to commit while the sketch has unapplied changes instead, so
  // the code that lands always matches the documents landing with it, and was seen.
  const reviewBeforeCommit = useCallback(() => setDiffOpen(true), []);

  /**
   * Watching is reading (PRD 8.2): poll the event log from the last seq while the
   * run is live. The runner never hears from us; reconnecting after a reload would
   * be the same range read from wherever the log left off.
   */
  useEffect(() => {
    if (!activeRun || !activeId) return;
    if (activeRun.status !== 'queued' && activeRun.status !== 'running') return;

    let lastSeq = runEvents.length > 0 ? runEvents[runEvents.length - 1]!.seq : -1;
    let stopped = false;

    const tick = async () => {
      try {
        const { status, events } = await fetchRunEvents(activeId, activeRun.id, lastSeq);
        if (stopped) return;
        if (events.length > 0) {
          lastSeq = events[events.length - 1]!.seq;
          setRunEvents((current) => [...current, ...events]);
        }
        if (status !== activeRun.status) {
          setActiveRun({ id: activeRun.id, status });
        }
      } catch {
        /* a missed poll is not a failed run; the next tick reads on */
      }
    };

    void tick();
    const interval = window.setInterval(() => void tick(), 800);
    return () => {
      stopped = true;
      window.clearInterval(interval);
    };
    // runEvents deliberately absent: lastSeq advances inside the closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRun?.id, activeRun?.status, activeId]);

  /**
   * A session may already be running — started before a reload, or from another
   * tab. Attaching is reading the same status the poll reads; a 404 or an
   * unconfigured service leaves the phase at idle, silently, because there is
   * nothing to attach to and nothing has gone wrong.
   */
  useEffect(() => {
    if (!activeId) return;
    const controller = new AbortController();
    void getSession(activeId, controller.signal)
      .then((status) => {
        if (!status || controller.signal.aborted) return;
        setAppSession({
          phase: status.processes.some((p) => p.running) ? 'live' : 'starting',
          previews: status.previews,
          boundaries: status.boundaries ?? [],
          processes: status.processes,
          filesVersion: status.filesVersion,
        });
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [activeId]);

  /**
   * Composition Run (docs/app-session.md): start the session, or attach to the one
   * already up — Run on a live session brings the pane back, it does not knock the
   * app over to start it again.
   */
  const beginSession = useCallback(async () => {
    if (!activeId) return;
    setPreviewOpen(true);
    if (appSession.phase === 'starting' || appSession.phase === 'live') return;
    setAppSession({ phase: 'starting', previews: [], boundaries: [], processes: [] });
    try {
      const { previews, boundaries } = await startSession(activeId);
      // Still 'starting': live is what the status poll says, not what the POST
      // hopes. Unless a stop raced the start — then the stop stands.
      setAppSession((cur) =>
        cur.phase === 'starting' ? { ...cur, previews, boundaries: boundaries ?? [] } : cur,
      );
    } catch (error) {
      // A start that timed out on the wire may still have started the app; one
      // probe settles it, and a session that answers is attached, not mourned.
      const status = await getSession(activeId).catch(() => undefined);
      if (status) {
        setAppSession({
          phase: status.processes.some((p) => p.running) ? 'live' : 'starting',
          previews: status.previews,
          boundaries: status.boundaries ?? [],
          processes: status.processes,
          filesVersion: status.filesVersion,
        });
        return;
      }
      // The pane is the surface for this failure (not just a toast): a transpile
      // error arrives as a paragraph of validator issues, and it needs the room.
      setAppSession({ phase: 'error', message: (error as Error).message });
    }
  }, [activeId, appSession.phase]);

  const endSession = useCallback(async () => {
    if (!activeId) return;
    try {
      await stopSession(activeId);
      setAppSession({ phase: 'idle' });
    } catch (error) {
      report({ title: 'Stop app', detail: (error as Error).message, refused: true });
    }
  }, [activeId, report]);

  /**
   * The status strip's heartbeat: while the pane is open on a session, ask where
   * every process stands. Watching is reading, the same rule as runs — the session
   * never hears from the poll. 'starting' holds until something is actually
   * running (or everything has already exited, which is also an answer).
   */
  useEffect(() => {
    if (!activeId || !previewOpen || !sessionActive) return;
    let stopped = false;

    const tick = async () => {
      try {
        const status = await getSession(activeId);
        if (stopped) return;
        if (!status) {
          // Gone: stopped elsewhere, or reaped idle. Idle is the honest state.
          setAppSession({ phase: 'idle' });
          return;
        }
        const someRunning = status.processes.some((p) => p.running);
        const allExited =
          status.processes.length > 0 && status.processes.every((p) => p.exitCode !== null);
        setAppSession((cur) => {
          if (cur.phase !== 'starting' && cur.phase !== 'live') return cur;
          return {
            phase: cur.phase === 'live' || someRunning || allExited ? 'live' : 'starting',
            previews: status.previews.length > 0 ? status.previews : cur.previews,
            boundaries: status.boundaries ?? cur.boundaries,
            processes: status.processes,
            filesVersion: status.filesVersion ?? cur.filesVersion,
          };
        });
      } catch {
        /* a missed poll is not a dead session; the next tick reads on */
      }
    };

    void tick();
    const interval = window.setInterval(() => void tick(), 3000);
    return () => {
      stopped = true;
      window.clearInterval(interval);
    };
  }, [activeId, previewOpen, sessionActive]);

  /** Node states for the canvas overlays, from the story so far. */
  const runStates = useMemo(() => {
    const states: Record<string, string> = {};
    const here = current.kind === 'graph' ? current.path : undefined;
    if (!here) return states;
    for (const event of runEvents) {
      // Top-level events carry no graphPath; subgraph events carry theirs.
      const at = event.graphPath ?? (activeRun ? runGraphRef.current : undefined);
      if (at !== here || !event.nodeId) continue;
      if (event.type === 'node.started') states[event.nodeId] = 'running';
      else if (event.type === 'node.finished' || event.type === 'node.output') states[event.nodeId] = 'finished';
      else if (event.type === 'node.failed') states[event.nodeId] = 'failed';
    }
    return states;
  }, [runEvents, current, activeRun]);

  const beginRun = useCallback(
    async (input: unknown) => {
      if (!activeId || current.kind !== 'graph') return;
      try {
        const run = await startRun(activeId, current.path, input);
        runGraphRef.current = current.path;
        setActiveRun({ id: run.id, status: run.status });
        setRunEvents([]);
        setRunLogOpen(true);
      } catch (error) {
        report({ title: 'Run', detail: (error as Error).message, refused: true });
      }
    },
    [activeId, current, report],
  );

  /**
   * Which manifest the current canvas is a view of. Every op needs it, and getting it
   * from the altitude rather than from the gesture is what keeps a graph edit out of
   * the composition file.
   */
  const manifestPath = useMemo(() => {
    const level = stack[stack.length - 1];
    if (level?.kind === 'graph') return level.path;
    return load.status === 'ready' ? load.bundle.compositionPath : undefined;
  }, [stack, load]);

  const runOps = useCallback(
    async (ops: ManifestOp[], title: string, opts?: { quiet?: boolean; path?: string }): Promise<boolean> => {
      // `path` sends the ops to another document than the canvas's manifest: a view's.
      const target = opts?.path ?? manifestPath;
      if (!activeId || !target) return false;
      try {
        const { summary, previous, hadPending } = await applyOps(activeId, target, ops);
        // Every application is one undoable step, whatever gesture produced it.
        undoStack.current.push({ path: target, previous, hadPending, summary });
        if (undoStack.current.length > UNDO_LIMIT) undoStack.current.shift();
        setUndoDepth(undoStack.current.length);
        await refresh();
        // Direct manipulation announces itself — the node moves, the edge appears,
        // the field changes — so `quiet` skips the toast there. Indirect invocations
        // (a key chord now, an agent later) keep it: their effect needs saying. The
        // summary still exists either way, which is what agent-first rule 4 requires
        // — reportable is about the string, not about always rendering it.
        if (!opts?.quiet) report({ title, detail: summary });
        return true;
      } catch (error) {
        // Refusals always speak, quiet or not: when a gesture does nothing, silence
        // reads as a bug.
        report({ title, detail: (error as Error).message, refused: true });
        return false;
      }
    },
    [activeId, manifestPath, refresh, report],
  );

  /**
   * PRD 7 lists Cmd+Z. An op's inverse is not computed — the file's previous text was
   * kept, and restoring a whole small manifest is simpler and safer than reversing a
   * splice. When the op was the first touch on the file, the pending row itself is
   * discarded, so undoing everything returns the project to "no pending changes".
   */
  const undoLast = useCallback(async () => {
    const entry = undoStack.current.pop();
    setUndoDepth(undoStack.current.length);
    if (!entry || !activeId) return;
    try {
      if (entry.hadPending) {
        await saveFile(activeId, entry.path, entry.previous);
      } else {
        await revertFile(activeId, entry.path);
      }
      // What was selected may be what was just unmade; a ghost selection keeps
      // Delete enabled against a node that is no longer there.
      clearSelection();
      await refresh();
      report({ title: 'Undo', detail: `Undid: ${entry.summary}` });
    } catch (error) {
      // The entry is already popped; putting it back would retry against whatever
      // just failed. Report and leave the file as it is.
      report({ title: 'Undo', detail: (error as Error).message, refused: true });
    }
  }, [activeId, refresh, report, clearSelection]);

  /**
   * What a confirmed Delete performs: every selected node (each cascading its own
   * edges), plus every selected edge whose fate is not already sealed by a selected
   * endpoint — one batch, one pending change, one undo step.
   */
  const performDelete = useCallback(async () => {
    if (!confirming) return;
    setConfirming(null);
    const graphOrComposition =
      current.kind === 'graph' ? bundle?.graphs[current.path] : bundle?.composition;
    const doomed = new Set(confirming.nodes);
    const edgeIds = confirming.edges.filter((id) => {
      const edge = graphOrComposition?.spec.edges.find((e) => e.id === id);
      return !edge || (!doomed.has(edge.from.node) && !doomed.has(edge.to.node));
    });
    const ops: ManifestOp[] = [
      ...confirming.nodes.map((id): ManifestOp => ({ op: 'removeNode', id })),
      ...edgeIds.map((id): ManifestOp => ({ op: 'removeEdge', id })),
      // Only for edges removed on their own; removeNode leaves exposes deliberately
      // (docs/ops.md), and a boundary removed takes its list with it.
      ...(current.kind === 'composition'
        ? exposesSync(bundle?.composition, { removedIds: edgeIds }).filter((op) => !doomed.has(op.id))
        : []),
    ];
    if (ops.length === 0) return;
    const ok = await runOps(ops, 'Delete', { quiet: true });
    if (ok) clearSelection();
  }, [confirming, current, bundle, runOps, clearSelection]);

  /**
   * Drawing an edge, with one added meaning: a flow edge is the moment a code
   * context becomes a step (PRD 5), and a step must have a callable boundary. If an
   * endpoint is a code node with no entrypoint, the gesture makes that true — a
   * typed identity handler written beside the node's files, and the field set, in
   * the same application. Never over anything that exists: if main.py is already
   * there it was written by someone with intentions, and the inspector is where an
   * entrypoint gets chosen deliberately.
   */
  const connectEdge = useCallback(
    async (edge: Record<string, unknown>) => {
      const ops: ManifestOp[] = [];

      if (edge['kind'] === 'flow' && activeId && bundle && manifestPath) {
        const graph = bundle.graphs[manifestPath];
        const known = new Set([...bundle.files, ...bundle.pending.map((p) => p.path)]);

        for (const end of ['from', 'to'] as const) {
          const endpoint = (edge[end] as { node?: string } | undefined)?.node;
          const node = graph?.spec.nodes.find((n) => n.id === endpoint);
          if (!node || node.type !== 'code' || node.entrypoint) continue;

          // The node's own directory: where its include already points, or a fresh
          // one named after it when the include list is empty.
          const glob = node.include[0];
          const starAt = glob ? glob.search(/[*?[]/) : -1;
          const dir = (starAt > 0 ? glob!.slice(0, starAt) : `src/${node.id.replace(/-/g, '_')}/`)
            .replace(/\/+$/, '');
          const path = `${dir}/main.py`;
          if (known.has(path)) continue;

          try {
            const { codeStub } = await import('./panes/AddNode.js');
            await saveFile(activeId, path, codeStub(node.id));
          } catch (error) {
            report({ title: 'Connect', detail: (error as Error).message, refused: true });
            return;
          }
          const patch: Record<string, unknown> = { entrypoint: path };
          if (node.include.length === 0) patch['include'] = [`${dir}/**/*.py`];
          ops.push({ op: 'updateNode', id: node.id, patch });
        }
      }

      ops.push({ op: 'addEdge', edge });
      if (current.kind === 'composition') ops.push(...exposesSync(bundle?.composition, { added: [edge] }));
      await runOps(ops, 'Connect', { quiet: true });
    },
    [activeId, bundle, manifestPath, current.kind, runOps, report],
  );

  const addNode = useCallback(
    async (node: Record<string, unknown>, scaffold: { path: string; content: string }[]) => {
      // A node whose references point at nothing arrives broken — unresolved
      // entrypoint, unresolved ref — so the files it needs are written first, as
      // pending changes. For a code context that file is a typed identity function:
      // the least prescriptive thing that still HAS an input and an output, since
      // PRD 7.2 keeps i and o in the source rather than in configuration. Paths
      // that already exist are left alone — a node may point at real code.
      if (activeId) {
        const existing = new Set([
          ...(bundle?.files ?? []),
          ...(bundle?.pending.map((p) => p.path) ?? []),
        ]);
        try {
          for (const file of scaffold) {
            if (!existing.has(file.path)) await saveFile(activeId, file.path, file.content);
          }
        } catch (error) {
          report({ title: 'Add node', detail: (error as Error).message, refused: true });
          return;
        }
      }
      await runOps(
        [
          { op: 'addNode', node },
          // Somewhere visible rather than at the origin. Where exactly is the open
          // question about what a command targets — see docs/roadmap.md.
          { op: 'setLayout', id: String(node['id']), x: 120, y: 420 },
        ],
        'Add node',
      );
    },
    [runOps, activeId, bundle, report, current],
  );


  // The running lift's progress, for the toast that follows it (Effect.live).
  // A read rebuilds the canvas views (Flow, Architecture); only they are under construction.
  const constructing = lifting && page === 'canvas';
  const liveProgress: LiveProgress | undefined = lifting
    ? {
        key: 'lift',
        text: liftProgressText(liftProgress),
        ...(liftProgress?.detail ? { detail: liftProgress.detail } : {}),
        fraction: liftProgressFraction(liftProgress),
      }
    : undefined;

  if (atHome) {
    return (
      <>
        <Toast effect={effect} progress={liveProgress} />
        {keyHelpOpen ? <KeyHelp context={commandContext} onClose={() => setKeyHelpOpen(false)} /> : null}
        <Home
          me={me}
          projects={projects}
          onOpen={enterProject}
          onChanged={reloadProjects}
          onOpenSettings={() => { setAtHome(false); openSettings(); }}
        />
        {settingsOpen ? null : null}
      </>
    );
  }

  return (
    <div
      className={[
        'shell',
        previewOpen ? 'with-preview' : '',
        panes.tree ? '' : 'no-tree',
        panes.inspector ? '' : 'no-inspector',
      ].filter(Boolean).join(' ')}
    >
      {/* While the repository is read, the stripes carry its progress over the views
          being rebuilt; the toast that would say the same waits for the result. On
          every other page (TL;DR, Chat, Files) nothing is under construction, so the
          page stays usable and the toast carries the progress instead. */}
      <Toast effect={constructing && effect?.live === 'lift' ? undefined : effect} progress={liveProgress} />
      {constructing && liveProgress ? (
        // Under construction: the canvas, loudly, for as long as the read takes.
        <div className="constructing" role="status" aria-live="polite">
          <div className="constructing-card">
            <p className="constructing-kicker">{liftTitle}</p>
            <p className="constructing-text">{liveProgress.text}</p>
            {liveProgress.detail ? <p className="constructing-detail">{liveProgress.detail}</p> : null}
            {liveProgress.fraction !== null ? <ProgressBar fraction={liveProgress.fraction} /> : null}
            <p className="constructing-note">This can take a few minutes. TL;DR, Chat and File Browser still work meanwhile.</p>
          </div>
        </div>
      ) : null}
      {keyHelpOpen ? <KeyHelp context={commandContext} onClose={() => setKeyHelpOpen(false)} /> : null}
      {diffOpen && bundle ? (
        <Suspense fallback={null}>
          <DiffPanel
            projectId={bundle.project.id}
            // Asserted, not assumed: `=== 'github'` fails closed when the field is
            // missing — an older server, a shape change — rather than offering to
            // commit something with no repository behind it.
            committable={bundle.project.sourceKind === 'github'}
            committing={committing}
            unapplied={bundle.generation?.state === 'stale' || applying}
            initialPath={diffFocus}
            onCommit={(message) => void commit(message)}
            onClose={() => {
              setDiffOpen(false);
              setDiffFocus(undefined);
            }}
          />
        </Suspense>
      ) : null}
      {runPanelOpen && bundle && current.kind === 'graph' ? (
        <RunPanel
          projectId={bundle.project.id}
          graphPath={current.path}
          onRun={(input) => void beginRun(input)}
          onClose={() => setRunPanelOpen(false)}
        />
      ) : null}
      {confirming ? (
        <ConfirmDelete
          nodes={confirming.nodes}
          edges={confirming.edges}
          cascades={(() => {
            const doc =
              current.kind === 'graph' ? bundle?.graphs[current.path] : bundle?.composition;
            const doomed = new Set(confirming.nodes);
            const named = new Set(confirming.edges);
            return (doc?.spec.edges ?? []).filter(
              (e) => !named.has(e.id) && (doomed.has(e.from.node) || doomed.has(e.to.node)),
            ).length;
          })()}
          onConfirm={() => void performDelete()}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
      {addNodeOpen && bundle ? (
        <AddNode
          docDir={docDir}
          altitude={current.kind === 'graph' ? 'graph' : 'composition'}
          existingIds={
            current.kind === 'graph'
              ? (bundle.graphs[current.path]?.spec.nodes ?? []).map((n) => n.id)
              : (bundle.composition?.spec.nodes ?? []).map((n) => n.id)
          }
          onAdd={(node, scaffold) => void addNode(node, scaffold)}
          onClose={() => setAddNodeOpen(false)}
        />
      ) : null}
      {/* The bar holds who and what: the project, Run, the account. */}
      <header className="top">
        {/* The way back out. */}
        <button type="button" className="brand brand-home" onClick={goHome} title="All projects">
          Civil
        </button>
        <span className="project-switch-wrap">
          <button
            type="button"
            className="project-switch"
            onClick={() => setPickerOpen((v) => !v)}
            title="Switch project, or open a repository"
          >
            {bundle?.project.name ?? 'No project'} <span className="project-switch-caret">▾</span>
          </button>
          {pickerOpen ? (
            <ProjectPicker
              projects={projects}
              activeId={activeId}
              onSelect={enterProject}
              onChanged={() => void reloadProjects().then((list) => {
                // Removing the open project leaves nothing selected; fall to the next.
                if (activeId && !list.some((p) => p.id === activeId)) setActiveId(list[0]?.id);
              })}
              onClose={() => setPickerOpen(false)}
            />
          ) : null}
        </span>
        <span className="spacer" />
        {/* One button, two meanings, split by altitude (docs/app-session.md):
            composition Run runs the app; graph Run runs the graph. */}
        <button
          className="run"
          type="button"
          disabled={runDisabled}
          title={runTitle}
          onClick={() =>
            current.kind === 'composition' ? void beginSession() : setRunPanelOpen(true)
          }
        >
          Run
        </button>
        <AccountMenu me={me} onOpenSettings={openSettings} />
      </header>

      {/* The views: the most prominent thing on a project, and how you move around it.
          The state of the project rides at the end of the same row. */}
      <div className="views">
        {/* The panels sit at the ends of the row they flank. */}
        <PaneToggle side="tree" shown={panes.tree} onToggle={() => run(commandById('pane.tree'))} />
        <div className="views-scroll">
        <nav className="views-tabs" aria-label="Views">
          {viewTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={tab.key === activeTab ? 'views-tab active' : 'views-tab'}
              aria-current={tab.key === activeTab ? 'page' : undefined}
              title={tab.title}
              onClick={() => goToTab(tab.key)}
            >
              {tab.name}
            </button>
          ))}
        </nav>
        <span className="views-status">
          {fatalCount > 0 ? (
            <span className="chip" title="Validation errors">
              <span className="dot danger" />
              {fatalCount} error{fatalCount === 1 ? '' : 's'}
            </span>
          ) : null}
          {/* The branch, and the way to pick up what has been pushed to it. Civil
              edits against a pinned commit, so this is deliberate rather than automatic. */}
          {bundle?.composition ? (
            <ApplyButton
              state={bundle.generation?.state}
              applying={applying}
              onApply={() => void doApply()}
            />
          ) : null}
          {/* Branch, sync and commit live in the Source control panel (left), always
              on screen — owner's rule, 2026-10-01: visible control of the git flow. */}
          {activeRun ? (
            <button
              type="button"
              className={`chip runchip runchip-${activeRun.status}`}
              onClick={() => setRunLogOpen((v) => !v)}
              title="Show or hide the run log"
            >
              <span className="dot" />
              run · {activeRun.status}
            </button>
          ) : null}
          {appSession.phase !== 'idle' ? (
            <button
              type="button"
              className={`chip runchip appchip-${appSession.phase}`}
              onClick={() => setPreviewOpen((v) => !v)}
              title="Show or hide the app preview"
            >
              <span className="dot" />
              app · {appSession.phase}
            </button>
          ) : null}
        </span>
        </div>
        {/* A read in progress, seen from a page it is not rebuilding: outside the
            scrolling tabs, so a phone shows it too, and one tap back to what it builds. */}
        {lifting && !constructing ? (
          <button
            type="button"
            className="chip readchip"
            onClick={() => goToTab('flow')}
            title={liveProgress ? `${liveProgress.text} — show Flow` : 'Reading the repository — show Flow'}
          >
            <span className="dot warn pulse" />
            Reading{liveProgress?.fraction != null ? ` ${Math.round(liveProgress.fraction * 100)}%` : '…'}
          </button>
        ) : null}
        <PaneToggle side="inspector" shown={panes.inspector} onToggle={() => run(commandById('pane.inspector'))} />
      </div>

      {/* Architecture's lenses, and where you are once you have stepped inside
          something; the top of a view needs no crumb. */}
      {page === 'canvas' && (activeTab === 'architecture' || !!altitudeHere || stack.length > 1 || (narrow && selectedIds.length === 1)) ? (
        <div className="context">
          {altitudeHere ? (
            // The flow's altitudes (docs/altitude.md): up re-describes, it does not nest.
            <span className="lenses altitudes" role="group" aria-label="Altitude">
              {flowAltitudes.map(({ altitude }) => (
                <button
                  key={altitude}
                  type="button"
                  className={altitude === altitudeHere ? 'lens active' : 'lens'}
                  aria-pressed={altitude === altitudeHere}
                  title={`${commandById(`altitude.${altitude}`).description} What is selected comes along.`}
                  onClick={() => run(commandById(`altitude.${altitude}`))}
                >
                  {ALTITUDE_NAMES[altitude]}
                </button>
              ))}
            </span>
          ) : null}
          {activeTab === 'architecture' && stack.length === 1 ? (
            <span className="lenses" role="group" aria-label="Lens">
              {lenses.map((lens) => (
                <button
                  key={lens.view ?? 'deployables'}
                  type="button"
                  className={lens.view === rootView ? 'lens active' : 'lens'}
                  aria-pressed={lens.view === rootView}
                  title={lens.title}
                  onClick={() => switchView(lens.view)}
                >
                  {lens.name}
                </button>
              ))}
            </span>
          ) : null}
          {stack.length > 1 ? (
          <nav className="breadcrumb" aria-label="Location">
            {stack.map((level, index) => (
              <span key={`${level.kind}-${index}`}>
                {index > 0 ? <span className="sep">/</span> : null}
                {index === stack.length - 1 ? (
                  <span className="here">{level.label}</span>
                ) : (
                  <button type="button" className="crumb" onClick={() => ascendTo(index)}>
                    {level.label}
                  </button>
                )}
              </span>
            ))}
          </nav>
          ) : null}
          <span className="spacer" />
          {activeTab === 'architecture' && stack.length === 1 && bundle && !bundle.views?.['architecture'] ? (
            <span className="context-note">
              The pieces are not written yet.
              {bundle.lift?.refusal ? null : (
                <button type="button" className="text-button" onClick={() => void doLiftRepo()} disabled={lifting}>
                  {lifting ? 'Reading the repository…' : liftTitle}
                </button>
              )}
            </span>
          ) : null}
          {narrow && current.kind !== 'code' && selectedIds.length === 1 ? (
            // No double-click on a phone: entering the selected node is a button there.
            <button type="button" className="chip" onClick={() => run(commandById('nav.descend'))}>
              Enter {selectedIds[0]} ›
            </button>
          ) : null}
        </div>
      ) : null}

      {narrow && (panes.tree || panes.inspector) ? (
        // Behind a panel that covers the canvas: a tap outside it closes it.
        <div className="drawer-scrim" onClick={closeOver} aria-hidden="true" />
      ) : null}

      <aside className="pane tree">
        {bundle ? (
          <SourceControl
            sourceKind={bundle.project.sourceKind}
            git={gitInfo}
            gitError={gitError}
            check={gitCheck}
            checking={checking}
            pending={bundle.pending}
            conflicts={bundle.conflicts ?? []}
            maintained={maintainedSet}
            applyState={bundle.generation?.state}
            applying={applying}
            committing={committing}
            note={commitNote}
            onCheck={() => void doCheck()}
            onSync={() => void doSync()}
            onApply={() => void doApply()}
            onLiftRepo={() => void doLiftRepo()}
            lifting={lifting}
            liftProgress={liveProgress}
            liftTitle={liftTitle}
            liftRefusal={bundle.lift?.refusal?.message ?? null}
            lifted={bundle.lift?.lifted ?? false}
            lastLift={lastLift && lastLift.projectId === activeId ? lastLift.text : null}
            onOpenFile={openFile}
            onCommit={(message) => void commit(message)}
            onReview={(path) => {
              setDiffFocus(path);
              setDiffOpen(true);
            }}
            onRevert={(path) => void doRevert(path)}
            onResolve={(path, side) => void doResolve(path, side)}
            branchMenuOpen={branchMenuOpen}
            branches={branchList}
            branchesError={branchesError}
            onToggleBranchMenu={() => setBranchMenuOpen((open) => !open)}
            onSwitchBranch={(name) => void runGitAction('Switch branch', (id) => switchToBranch(id, name))}
            onCreateBranch={(name) => void runGitAction('Create branch', (id) => createBranch(id, name))}
            prFormOpen={prFormOpen}
            onTogglePrForm={() => setPrFormOpen((open) => !open)}
            onOpenPr={(title) => void runGitAction('Pull request', (id) => openPullRequest(id, title))}
            gitBusy={gitBusy}
          />
        ) : null}
        {/* The files live in the File Browser (§25); here, only the way in. */}
        <ProjectTree
          bundle={bundle}
          onOpenFile={openFile}
          onInitialize={() => void initialize()}
          initializing={initializing}
          onLiftRepo={() => void doLiftRepo()}
          lifting={lifting}
          show="setup"
        />
      </aside>

      <main className="centre">
        {load.status === 'loading' ? null : load.status === 'error' ? (
          <div className="empty">
            <h2>Could not load the project</h2>
            <p>{load.message}</p>
          </div>
        ) : load.status === 'empty' ? (
          <div className="empty">
            <h2>No project open</h2>
            <p>Open one of your repositories, or start from an example.</p>
            <button type="button" className="connect" onClick={() => setPickerOpen(true)}>
              Choose a project
            </button>
          </div>
        ) : page === 'files' ? (
          <div className="page files">
            <aside className="files-tree">
              <ProjectTree
                bundle={load.bundle}
                onOpenFile={openFile}
                onInitialize={() => void initialize()}
                initializing={initializing}
                onLiftRepo={() => void doLiftRepo()}
                lifting={lifting}
                show="files"
                activePath={browsing.active}
              />
            </aside>
            <div className="files-editor">
              {browsing.files.length > 0 ? (
                <Suspense fallback={<div className="code-empty">Loading editor…</div>}>
                  <CodeContext
                    projectId={load.bundle.project.id}
                    files={browsing.files}
                    active={browsing.active}
                    reveal={browsing.reveal}
                    maintained={maintainedSet}
                    showList={false}
                    onActiveChange={reportBrowsingFile}
                    onPendingChanged={codeSaved}
                  />
                </Suspense>
              ) : (
                <div className="code-empty">Choose a file.</div>
              )}
            </div>
          </div>
        ) : page === 'canvas' && rootView === 'flow' && !load.bundle.views?.['flow'] ? (
          <div className="page tldr">
            <div className="tldr-body">
              <p className="page-kicker">Flow</p>
              <p className="tldr-text is-quiet">
                The flow is not written yet: what the application does, the mental model to start in.
                Civil writes it by reading the repository.
              </p>
              {load.bundle.lift?.refusal ? (
                <p className="page-note">{load.bundle.lift.refusal.message}</p>
              ) : (
                <p className="page-note">
                  <button type="button" className="text-button" onClick={() => void doLiftRepo()} disabled={lifting}>
                    {lifting ? 'Reading the repository…' : liftTitle}
                  </button>
                </p>
              )}
            </div>
          </div>
        ) : page === 'tldr' ? (
          <Tldr
            name={load.bundle.project.name}
            state={activeId ? tldrs[activeId] : undefined}
            onRegenerate={() => run(commandById('tldr.regenerate'))}
            {...(load.bundle.lift?.refusal ? {} : { onLift: () => void doLiftRepo() })}
            liftTitle={liftTitle}
          />
        ) : page === 'ui' ? (
          <UiTab
            ref={uiRef}
            key={load.bundle.project.id}
            projectId={load.bundle.project.id}
            frontends={uiFrontends}
            sharedLinks={load.bundle.ui?.links ?? {}}
            linksError={load.bundle.ui?.error}
            onEffect={uiEffect}
            onSharedLinksChanged={() => void refresh()}
            onState={setUiTab}
            onOpenFile={(path, line) => {
              setPage('canvas');
              openFile(path, line);
            }}
            me={me}
          />
        ) : page === 'chat' ? (
          <Chat
            state={activeId ? chats[activeId] : undefined}
            onAsk={(question) => activeId && askQuestion(activeId, question)}
            onNew={() => run(commandById('chat.new'))}
            isPart={(id) => partIds.has(id)}
            isFile={(path) => fileSet.has(path)}
            onOpenPart={openPart}
            onOpenFile={(path, line) => {
              setPage('canvas');
              openFile(path, line);
            }}
          />
        ) : current.kind === 'code' ? (
          // PRD 7: a viewport takeover, not a panel. The canvas is still on the stack
          // underneath, so Escape returns you to exactly where you left it.
          <Suspense fallback={<div className="code-empty">Loading editor…</div>}>
            <CodeContext
              projectId={load.bundle.project.id}
              files={current.files}
              {...(current.groups ? { groups: current.groups } : {})}
              active={current.active}
              reveal={current.reveal}
              maintained={maintainedSet}
              onActiveChange={reportActiveFile}
              onPendingChanged={codeSaved}
            />
          </Suspense>
        ) : (
          <>
          {isLegacy ? (
            <div className="drift-banner">
              <span>
                This project keeps its documents at the repo root. Move them into <code>civil/</code>?
              </span>
              <span className="drift-actions">
                <button type="button" className="connect" onClick={() => void doMigrate()}>
                  Move into civil/
                </button>
                <button type="button" className="link" onClick={() => setMigrateDismissed(true)}>
                  dismiss
                </button>
              </span>
            </div>
          ) : null}
          {driftedOrchestration && !isLegacy ? (
            <div className="drift-banner">
              <span>
                This graph's code (<code>{driftedOrchestration}</code>) was changed outside Civil.
              </span>
              <span className="drift-actions">
                <button type="button" className="connect" onClick={() => void doLift()}>
                  Update graph from code
                </button>
                <button type="button" className="connect" onClick={() => void doApply()}>
                  Regenerate code from graph
                </button>
                <button
                  type="button"
                  className="link"
                  onClick={() =>
                    setDriftDismissed((prev) => new Set(prev).add(driftedOrchestration))
                  }
                >
                  dismiss
                </button>
              </span>
            </div>
          ) : null}
          <Editor
            bundle={load.bundle}
            stack={stack}
            selectedIds={selectedIds}
            selectedEdgeIds={selectedEdgeIds}
            runStates={runStates}
            onDescend={descend}
            onAscend={ascend}
            onSelectionChange={(nodes, edgeIds) => {
              setSelectedIds(nodes);
              setSelectedEdgeIds(edgeIds);
            }}
            onRegisterActions={(actions) => {
              canvasActions.current = actions;
            }}
            onConnect={(edge) => void connectEdge(edge)}
            onRefuse={(reason) => report({ title: 'Connect', detail: reason, refused: true })}
            onMoveNode={(id, x, y, path) => void runOps([{ op: 'setLayout', id, x, y }], 'Move', { quiet: true, ...(path ? { path } : {}) })}
            onEnterLeaf={(id) => {
              // A Story moment is not a door: double-clicking it goes down an altitude.
              if (altitudeHere !== 'story') return;
              const detail = stepAltitude(1, [id]);
              if (detail) report({ title: 'Down an altitude', detail });
            }}
          />
          </>
        )}
        {/* One drawer at a time: they share the same strip of screen, and the run
            log — the more momentary of the two — wins while both are open. */}
        {activeRun && runLogOpen && page === 'canvas' && current.kind !== 'code' ? (
          <RunLog
            status={activeRun.status}
            events={runEvents}
            onCancel={() => activeId && void cancelRun(activeId, activeRun.id).catch(() => undefined)}
            onClose={() => setRunLogOpen(false)}
          />
        ) : activeId && sessionLogOpen && appSession.phase !== 'idle' && page === 'canvas' && current.kind !== 'code' ? (
          <SessionLog
            projectId={activeId}
            live={sessionActive}
            onClose={() => setSessionLogOpen(false)}
          />
        ) : null}
      </main>

      {previewOpen ? (
        <PreviewPane
          session={appSession}
          logsOpen={sessionLogOpen}
          onToggleLogs={() => setSessionLogOpen((v) => !v)}
          onStart={() => void beginSession()}
          onStop={() => void endSession()}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}

      <aside className="pane inspector">
        {settingsOpen ? (
          <Settings me={me} onClose={() => setSettingsOpen(false)} />
        ) : (
          <Inspector
            bundle={bundle}
            altitude={current}
            selectedIds={selectedIds}
            selectedEdgeIds={selectedEdgeIds}
            onPatch={(id, patch) => void runOps([{ op: 'updateNode', id, patch }], 'Edit', { quiet: true })}
            onPatchEdge={(id, patch) => void runOps([{ op: 'updateEdge', id, patch }], 'Edit', { quiet: true })}
            onRename={(from, to) =>
              void runOps([{ op: 'renameNode', from, to }], 'Rename', { quiet: true }).then((ok) => {
                // The selection follows the node to its new name — unless the user
                // has already selected something else mid-flight.
                if (ok) setSelectedIds((cur) => cur.map((id) => (id === from ? to : id)));
              })
            }
            // The prompt is a file; saving it rides the same route as a Monaco save,
            // so pending, the diff panel, and a live session's hot reload all follow.
            onOpenFile={openFile}
            onSavePrompt={async (path, content) => {
              if (!activeId) return false;
              try {
                await saveFile(activeId, path, content);
                await refresh();
                return true;
              } catch (error) {
                report({ title: 'Save prompt', detail: (error as Error).message, refused: true });
                return false;
              }
            }}
            onGoTo={goToNode}
            // The inspector's remove buttons ask the same question the Delete key asks.
            onRemoveNode={(id) => setConfirming({ nodes: [id], edges: [] })}
            onRemoveEdge={(id) =>
              void runOps(
                [
                  { op: 'removeEdge', id },
                  ...(current.kind === 'composition'
                    ? exposesSync(bundle?.composition, { removedIds: [id] })
                    : []),
                ],
                'Disconnect',
                { quiet: true },
              ).then((ok) => {
                if (ok) setSelectedEdgeIds((cur) => cur.filter((e) => e !== id));
              })
            }
          />
        )}
      </aside>
    </div>
  );
}

/** The button for one side panel: its outline, with the panel's side filled while it is shown. */
function PaneToggle({ side, shown, onToggle }: { side: 'tree' | 'inspector'; shown: boolean; onToggle: () => void }) {
  const name = side === 'tree' ? 'project panel' : 'inspector';
  const chord = formatChord(commandById(side === 'tree' ? 'pane.tree' : 'pane.inspector').keys[0]!);
  const x = side === 'tree' ? 1.5 : 9.5;
  return (
    <button
      type="button"
      className="pane-toggle"
      onClick={onToggle}
      aria-pressed={shown}
      aria-label={`${shown ? 'Hide' : 'Show'} the ${name}`}
      title={`${shown ? 'Hide' : 'Show'} the ${name} (${chord})`}
    >
      <svg width="16" height="12" viewBox="0 0 16 12" aria-hidden="true">
        <rect x="0.5" y="0.5" width="15" height="11" fill="none" stroke="currentColor" />
        <rect x={x} y="1.5" width="5" height="9" fill={shown ? 'currentColor' : 'none'} />
        <line x1={side === 'tree' ? 6.5 : 9.5} y1="0.5" x2={side === 'tree' ? 6.5 : 9.5} y2="11.5" stroke="currentColor" />
      </svg>
    </button>
  );
}
